import React, { useEffect, useMemo, useState } from "react";
import { Users, ShoppingCart, Clock, User } from "lucide-react";
import { getCurrentUser } from "../lib/pocketbase";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import PersianDatePicker from "../components/ui/PersianDatePicker";
import {
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableHeaderCell,
} from "../components/ui/Table";

const PER_PAGE = 5;

type DashboardTab = "users" | "orders";

const toPersianDigits = (value: number | string) =>
  String(value).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

const normalizeForFilter = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)));

const includesFilter = (haystack: string, needle: string) => {
  if (!needle.trim()) return true;
  return normalizeForFilter(haystack).includes(normalizeForFilter(needle));
};

const FIRST_NAMES = [
  "علی",
  "مریم",
  "حسین",
  "زهرا",
  "محمد",
  "فاطمه",
  "رضا",
  "سارا",
  "امیر",
  "نرگس",
  "پویا",
  "الهام",
  "مهدی",
  "شیما",
  "کامران",
  "ندا",
  "بهرام",
  "لیلا",
];

const LAST_NAMES = [
  "رضایی",
  "احمدی",
  "کریمی",
  "حسینی",
  "محمدی",
  "جعفری",
  "موسوی",
  "نوری",
  "صادقی",
  "اکبری",
];

const ORDER_TITLES = [
  "انگشتر سفارشی طلا",
  "گردنبند طرح گل",
  "دستبند چرم و طلا",
  "آویز ماه تولد",
  "گوشواره ظریف",
  "ست حلقه عروسی",
  "مدال نام طلا",
  "زنجیر کارتیه",
  "انگشتر نگین دار",
  "پلاک طلا با حکاکی",
];

const ORDER_STATUSES: {
  status: string;
  statusLabel: string;
}[] = [
  { status: "pending", statusLabel: "در انتظار" },
  { status: "onProgress", statusLabel: "در حال ساخت" },
  { status: "done", statusLabel: "آماده تحویل" },
  { status: "delivery", statusLabel: "در حال ارسال" },
];

const DEMO_SHOP_USERS = Array.from({ length: 18 }, (_, i) => {
  const first = FIRST_NAMES[i % FIRST_NAMES.length];
  const last = LAST_NAMES[i % LAST_NAMES.length];
  return {
    id: `u${i + 1}`,
    name: `${first} ${last}`,
    phone: `0912${String(1000000 + i).slice(-7)}`,
    ordersCount: (i % 4) + 1,
  };
});

const DEMO_SHOP_ORDERS = Array.from({ length: 22 }, (_, i) => {
  const user = DEMO_SHOP_USERS[i % DEMO_SHOP_USERS.length];
  const statusInfo = ORDER_STATUSES[i % ORDER_STATUSES.length];
  const day = 21 - (i % 14);
  return {
    id: `ord-${100 + i}`,
    title: ORDER_TITLES[i % ORDER_TITLES.length],
    userName: user.name,
    status: statusInfo.status,
    statusLabel: statusInfo.statusLabel,
    created: `۱۴۰۴/۰۷/${toPersianDigits(day).padStart(2, "۰")}`,
  };
});

function paginate<T>(items: T[], page: number, perPage: number) {
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * perPage;
  return {
    slice: items.slice(start, start + perPage),
    totalPages,
    page: safePage,
    totalItems: items.length,
  };
}

interface PaginationBarProps {
  page: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

const PaginationBar: React.FC<PaginationBarProps> = ({
  page,
  totalPages,
  totalItems,
  onPageChange,
}) => {
  const from = totalItems === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const to = Math.min(page * PER_PAGE, totalItems);

  return (
    <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="text-sm text-gray-600">
        نمایش <span className="font-medium">{from}</span> تا{" "}
        <span className="font-medium">{to}</span> از{" "}
        <span className="font-medium">{totalItems}</span> مورد
      </p>
      <div className="flex items-center space-x-2 space-x-reverse">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          قبلی
        </button>
        <span className="px-3 py-2 text-sm text-gray-700">
          صفحه {page} از {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          بعدی
        </button>
      </div>
    </div>
  );
};

const ShopDashboardPage: React.FC = () => {
  const currentUser = getCurrentUser() as { title?: string; email?: string };
  const shopName = currentUser?.title || currentUser?.email || "فروشگاه";

  const [activeTab, setActiveTab] = useState<DashboardTab>("users");
  const [usersPage, setUsersPage] = useState(1);
  const [ordersPage, setOrdersPage] = useState(1);

  const [userFilters, setUserFilters] = useState({
    name: "",
    phone: "",
  });

  const [orderFilters, setOrderFilters] = useState({
    title: "",
    userName: "",
    status: "",
    date: "",
  });

  const filteredUsers = useMemo(
    () =>
      DEMO_SHOP_USERS.filter(
        (user) =>
          includesFilter(user.name, userFilters.name) &&
          includesFilter(user.phone, userFilters.phone)
      ),
    [userFilters.name, userFilters.phone]
  );

  const filteredOrders = useMemo(
    () =>
      DEMO_SHOP_ORDERS.filter((order) => {
        if (
          orderFilters.status &&
          order.status !== orderFilters.status
        ) {
          return false;
        }
        const dateMatch =
          !orderFilters.date ||
          normalizeForFilter(order.created) ===
            normalizeForFilter(orderFilters.date);
        return (
          includesFilter(order.title, orderFilters.title) &&
          includesFilter(order.userName, orderFilters.userName) &&
          dateMatch
        );
      }),
    [
      orderFilters.title,
      orderFilters.userName,
      orderFilters.status,
      orderFilters.date,
    ]
  );

  useEffect(() => {
    setUsersPage(1);
  }, [userFilters.name, userFilters.phone]);

  useEffect(() => {
    setOrdersPage(1);
  }, [
    orderFilters.title,
    orderFilters.userName,
    orderFilters.status,
    orderFilters.date,
  ]);

  const usersPagination = useMemo(
    () => paginate(filteredUsers, usersPage, PER_PAGE),
    [filteredUsers, usersPage]
  );

  const ordersPagination = useMemo(
    () => paginate(filteredOrders, ordersPage, PER_PAGE),
    [filteredOrders, ordersPage]
  );

  const statusFilterOptions = [
    { value: "", label: "همه وضعیت‌ها" },
    ...ORDER_STATUSES.map((s) => ({
      value: s.status,
      label: s.statusLabel,
    })),
  ];

  const stats = [
    {
      name: "کاربران این فروشگاه",
      value: DEMO_SHOP_USERS.length,
      icon: Users,
      color: "bg-blue-500",
    },
    {
      name: "سفارشات ثبت‌شده",
      value: DEMO_SHOP_ORDERS.length,
      icon: ShoppingCart,
      color: "bg-green-500",
    },
    {
      name: "در حال پیگیری",
      value: DEMO_SHOP_ORDERS.filter((o) => o.status !== "done").length,
      icon: Clock,
      color: "bg-amber-500",
    },
  ];

  const handleTabChange = (tab: DashboardTab) => {
    setActiveTab(tab);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          داشبورد فروشگاه
        </h1>
        <p className="text-gray-600">
          {shopName} — کاربران و سفارش‌های مرتبط با این فروشگاه (نمای دمو)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="card p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="mr-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 space-y-4">
          <div className="flex rounded-lg bg-gray-100 p-1 max-w-md">
            <button
              type="button"
              onClick={() => handleTabChange("users")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === "users"
                  ? "bg-white text-primary-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <User className="h-4 w-4" />
              کاربران
            </button>
            <button
              type="button"
              onClick={() => handleTabChange("orders")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === "orders"
                  ? "bg-white text-primary-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <ShoppingCart className="h-4 w-4" />
              سفارشات
            </button>
          </div>
        </div>

        {activeTab === "users" ? (
          <>
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/80">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="نام"
                  placeholder="جستجو بر اساس نام..."
                  value={userFilters.name}
                  onChange={(e) =>
                    setUserFilters((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
                <Input
                  label="موبایل"
                  placeholder="مثال: 0912 یا ۰۹۱۲..."
                  value={userFilters.phone}
                  onChange={(e) =>
                    setUserFilters((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableHeaderCell>نام</TableHeaderCell>
                <TableHeaderCell>موبایل</TableHeaderCell>
                <TableHeaderCell>تعداد سفارش</TableHeaderCell>
              </TableHeader>
              <tbody className="bg-white divide-y divide-gray-200">
                {usersPagination.slice.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-6 py-8 text-center text-sm text-gray-500"
                    >
                      کاربری با این فیلترها پیدا نشد.
                    </td>
                  </tr>
                ) : (
                  usersPagination.slice.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="text-gray-900">
                        {user.name}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {user.phone}
                      </TableCell>
                      <TableCell>{user.ordersCount}</TableCell>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
            <PaginationBar
              page={usersPagination.page}
              totalPages={usersPagination.totalPages}
              totalItems={usersPagination.totalItems}
              onPageChange={setUsersPage}
            />
          </>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/80">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Input
                  label="عنوان"
                  placeholder="جستجو عنوان سفارش..."
                  value={orderFilters.title}
                  onChange={(e) =>
                    setOrderFilters((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                />
                <Input
                  label="کاربر"
                  placeholder="نام مشتری..."
                  value={orderFilters.userName}
                  onChange={(e) =>
                    setOrderFilters((prev) => ({
                      ...prev,
                      userName: e.target.value,
                    }))
                  }
                />
                <Select
                  label="وضعیت"
                  options={statusFilterOptions}
                  value={orderFilters.status}
                  onChange={(e) =>
                    setOrderFilters((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                />
                <PersianDatePicker
                  label="تاریخ"
                  placeholder="انتخاب تاریخ شمسی"
                  value={orderFilters.date}
                  onChange={(date) =>
                    setOrderFilters((prev) => ({
                      ...prev,
                      date,
                    }))
                  }
                />
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableHeaderCell>عنوان</TableHeaderCell>
                <TableHeaderCell>کاربر</TableHeaderCell>
                <TableHeaderCell>وضعیت</TableHeaderCell>
                <TableHeaderCell>تاریخ</TableHeaderCell>
              </TableHeader>
              <tbody className="bg-white divide-y divide-gray-200">
                {ordersPagination.slice.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-8 text-center text-sm text-gray-500"
                    >
                      سفارشی با این فیلترها پیدا نشد.
                    </td>
                  </tr>
                ) : (
                  ordersPagination.slice.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="text-gray-900">
                        {order.title}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {order.userName}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            order.status === "done"
                              ? "bg-green-100 text-green-800"
                              : order.status === "onProgress"
                                ? "bg-blue-100 text-blue-800"
                                : order.status === "delivery"
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {order.statusLabel}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {order.created}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
            <PaginationBar
              page={ordersPagination.page}
              totalPages={ordersPagination.totalPages}
              totalItems={ordersPagination.totalItems}
              onPageChange={setOrdersPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ShopDashboardPage;
